import React, { useState, useRef } from "react";
import { Db, storage } from "./../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Highlight from "@tiptap/extension-highlight";
import "./Admin.css";

const PostEditor = () => {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [language, setLanguage] = useState("");
  const [genre, setGenre] = useState("");
  const [customGenre, setCustomGenre] = useState("");
  const [columnName, setColumnName] = useState("");
  const [mainImage, setMainImage] = useState(null);
  const [author, setAuthor] = useState("");
  const [socials, setSocials] = useState({
    instagram: "",
    facebook: "",
    youtube: "",
    spotify: "",
  });
  const [loading, setLoading] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const fileInputRef = useRef();

  const genres = ["Death Metal", "Heavy Metal", "Black Metal"];

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ history: true }),
      Underline,
      Link.configure({ openOnClick: false }),
      Image.configure({ inline: false, allowBase64: true }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TextStyle,
      Highlight,
    ],
    content: "<p>Start writing...</p>",
  });

  const addLink = () => {
    if (!linkUrl) return;
    editor.chain().focus().extendMarkRange("link").setLink({ href: linkUrl }).run();
    setLinkUrl("");
    setShowLinkInput(false);
  };

  const insertImage = async (file) => {
    if (!file) return;
    const imageRef = ref(storage, `inlineImages/${Date.now()}_${file.name}`);
    await uploadBytes(imageRef, file);
    const imageUrl = await getDownloadURL(imageRef);
    editor.chain().focus().setImage({ src: imageUrl }).run();
  };

  const handleSocialChange = (e) => {
    setSocials({ ...socials, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editor || !mainImage) return alert("Add an image and content!");
    if (!author.trim()) return alert("Add the author's name!");
    if (category === 'dailycolumn' && !columnName.trim()) return alert("You must define the Column Name!");
    if (!language) return alert("You must select a language!");

    setLoading(true);

    try {
      const imageRef = ref(storage, `posts/${Date.now()}_${mainImage.name}`);
      await uploadBytes(imageRef, mainImage);
      const mainImageUrl = await getDownloadURL(imageRef);

      const finalGenre = genre === "custom" ? customGenre : genre;
      const hideSocials = category === 'news' || category === 'topnews' || category === 'dailycolumn' || category === 'specialprojects';

      const slug = title
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");

      await addDoc(collection(Db, "posts"), {
        title,
        slug,
        category,
        language,
        genre: finalGenre || null,
        columnName: category === 'dailycolumn' ? columnName : null,
        author,
        socials: hideSocials ? {} : Object.fromEntries(Object.entries(socials).filter(([_, url]) => url.trim())),
        content: editor.getHTML(),
        mainImageUrl,
        createdAt: serverTimestamp(),
        status: "draft",
      });

      alert("✅ Post saved!");
      setTitle("");
      setCategory("");
      setLanguage("");
      setGenre("");
      setCustomGenre("");
      setColumnName("");
      setAuthor("");
      setSocials({ instagram: "", facebook: "", youtube: "", spotify: "" });
      setMainImage(null);
      editor.commands.clearContent();
    } catch (err) {
      console.error(err);
      alert("❌ Error while saving");
    } finally {
      setLoading(false);
    }
  };

  if (!editor) return null;

  const hideSocials = category === 'news' || category === 'topnews' || category === 'dailycolumn' || category === 'specialprojects';
  const showGenreSelect = category === "reviews" || category === "interviews";
  const showColumnNameInput = category === "dailycolumn";

  return (
    <div className="editor-container p-4">
      <h2 className="mb-3">📝 New Post(Daria you cant upload  3 paragraphs with just the word rat , thanks)</h2>

      <form onSubmit={handleSubmit}>
        {/* Title */}
        <input
          type="text"
          placeholder="Title"
          className="form-control mb-3"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />

        {/* Category */}
        <select
          className="form-select mb-3"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          required
        >
          <option value="">Select Category</option>
          <option value="topnews">Top News</option>
          <option value="reviews">Reviews</option>
          <option value="interviews">Interviews</option>
          <option value="news">News</option>
          <option value="features">Features</option>
          <option value="dailycolumn">Daily Column</option>
          <option value="specialprojects">Special Projects</option>
        </select>

        {/* Language */}
        <select
          className="form-select mb-3"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          required
        >
          <option value="">Select Language</option>
          <option value="el">Greek</option>
          <option value="en">English</option>
          <option value="other">Other</option>
        </select>

        {/* Column Name */}
        {showColumnNameInput && (
          <input
            type="text"
            className="form-control mb-3"
            placeholder="Column Name (e.g. Metal on Screen)"
            value={columnName}
            onChange={(e) => setColumnName(e.target.value)}
            required
          />
        )}

        {/* Genre */}
        {showGenreSelect && (
          <>
            <select
              className="form-select mb-3"
              value={genre}
              onChange={(e) => setGenre(e.target.value)}
              required
            >
              <option value="">Select Metal Subgenre</option>
              {genres.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
              <option value="custom">+ Add New Subgenre</option>
            </select>

            {genre === "custom" && (
              <input
                type="text"
                className="form-control mb-3"
                placeholder="Type new subgenre..."
                value={customGenre}
                onChange={(e) => setCustomGenre(e.target.value)}
                required
              />
            )}
          </>
        )}

        {/* Author */}
        <input
          type="text"
          placeholder="Author Name"
          className="form-control mb-3"
          value={author}
          onChange={(e) => setAuthor(e.target.value)}
          required
        />

        {/* Social Links */}
        {!hideSocials && (
          <div className="mb-3">
            <label className="form-label">🔗 Social Links (optional)</label>
            {["instagram", "facebook", "youtube", "spotify"].map((s) => (
              <input
                key={s}
                type="url"
                name={s}
                placeholder={`${s.charAt(0).toUpperCase() + s.slice(1)} URL`}
                className="form-control mb-2"
                value={socials[s]}
                onChange={handleSocialChange}
              />
            ))}
          </div>
        )}

        {/* Main Image */}
        <label className="form-label">📸 Main Image</label>
        <input
          type="file"
          accept="image/*"
          className="form-control mb-3"
          onChange={(e) => setMainImage(e.target.files[0])}
          required
        />

        {/* Toolbar */}
        <div className="toolbar mb-2">
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={editor.isActive("bold") ? "is-active" : ""}
          >
            B
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={editor.isActive("italic") ? "is-active" : ""}
          >
            I
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={editor.isActive("underline") ? "is-active" : ""}
          >
            U
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={editor.isActive("bulletList") ? "is-active" : ""}
          >
            ●
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={editor.isActive("orderedList") ? "is-active" : ""}
          >
            1.
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHighlight({ color: "#fef08a" }).run()}
            className={editor.isActive("highlight") ? "is-active" : ""}
          >
            Highlight
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className={editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}
          >
            Align Left
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className={editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}
          >
            Align Center
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            className={editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}
          >
            Align Right
          </button>

          <button type="button" onClick={() => setShowLinkInput(true)}>🔗 Link</button>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={(e) => insertImage(e.target.files[0])}
            style={{ display: "none" }}
          />
          <button type="button" onClick={() => fileInputRef.current.click()}>🖼️ Image</button>

          {showLinkInput && (
            <div className="link-input-bar">
              <input
                type="url"
                placeholder="URL"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                className="form-control form-control-sm me-2"
              />
              <button type="button" onClick={addLink} className="btn btn-sm btn-primary">Add</button>
              <button type="button" onClick={() => setShowLinkInput(false)} className="btn btn-sm btn-secondary">Cancel</button>
            </div>
          )}
        </div>

        {/* Editor */}
        <EditorContent editor={editor} className="editor mb-3" />

        <button type="submit" disabled={loading} className="btn btn-success w-100">
          {loading ? "Uploading..." : "💾 Save"}
        </button>
      </form>
    </div>
  );
};

export default PostEditor;
